import { useEffect, useState } from 'react';
import type { Preferences } from '@forecastify/shared';
import {
  Icon,
  SWATCH_ROLES,
  THEMES,
  applyTheme,
  readThemeSwatches,
  type ThemeSwatch,
} from '@forecastify/ui';
import { useT, type MessageKey } from '../../i18n';
import { Card, CardColumn } from '../card';
import { rememberTheme } from '../../preferences-boot';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️ THE THEME GALLERY. Swatches DERIVED, not forty hex literals.            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `docs/plan/debt.md` has wanted this since C1, and the mockup shows why: it carries a `pv` map per
 * theme — `{ bg: '#ffffff', sidebar: '#f7f7f9', card: '#ffffff', accent: '#6d3fd4', … }` — which is
 * eight themes × five roles of hardcoded colour. **A hardcoded preview is a second definition of the
 * palette**, so changing a token leaves the swatch showing the old one: the single screen whose
 * whole job is showing you what a theme looks like becomes the one screen that lies about it.
 *
 * `readThemeSwatches` measures them instead, by applying each theme to an offscreen probe and
 * reading the computed custom properties. Whatever the token layer resolves to today is what the
 * swatch shows, with no list to keep in step. It also satisfies the design system's `no-raw-hex`,
 * which forty literals would break outright.
 *
 * ⚠️ **It needs a real stylesheet, so under jsdom there is nothing to read** — the helper reports
 * that by returning no entry rather than inventing a colour, and the tile falls back to a labelled
 * placeholder. A test asserting specific hex in jsdom would be asserting nothing.
 */

/** `theme.<id>.label` / `.hint`, as literal keys — never `t(\`theme.${id}.label\`)`. */
const THEME_LABEL: Readonly<Record<string, MessageKey>> = {
  light: 'theme.light.label',
  dark: 'theme.dark.label',
  latte: 'theme.latte.label',
  mocha: 'theme.mocha.label',
  nord: 'theme.nord.label',
  frappe: 'theme.frappe.label',
  macchiato: 'theme.macchiato.label',
  radix: 'theme.radix.label',
};

const THEME_HINT: Readonly<Record<string, MessageKey>> = {
  light: 'theme.light.hint',
  dark: 'theme.dark.hint',
  latte: 'theme.latte.hint',
  mocha: 'theme.mocha.hint',
  nord: 'theme.nord.hint',
  frappe: 'theme.frappe.hint',
  macchiato: 'theme.macchiato.hint',
  radix: 'theme.radix.hint',
};

/** The mockup's own preview: a page with a sidebar, a card on it, and an accent mark. */
function Swatch({ swatch }: { readonly swatch: ThemeSwatch | undefined }) {
  const t = useT();

  if (swatch === undefined) {
    return (
      <div className="bg-muted grid h-16 place-items-center rounded-md border">
        <span className="text-muted-foreground px-2 text-center text-[10.5px]">
          {t('account.theme.noPreview')}
        </span>
      </div>
    );
  }

  return (
    /* ⚠️ Inline styles, which is the one place they are correct: these values were measured at
     * runtime and cannot be class names. `aria-hidden` because the labels beside it already say
     * which theme this is — a screen reader announcing five colour strings would be noise. */
    <div
      aria-hidden
      className="flex h-16 overflow-hidden rounded-md border"
      style={{ background: swatch.background }}
    >
      <div className="w-1/4" style={{ background: swatch.sidebar }} />
      <div className="flex flex-1 items-center gap-1.5 p-2">
        <div
          className="flex h-full flex-1 items-center gap-1.5 rounded p-1.5"
          style={{ background: swatch.card }}
        >
          <span className="size-2 rounded-full" style={{ background: swatch.primary }} />
          <span
            className="h-1 flex-1 rounded"
            style={{ background: swatch.foreground, opacity: 0.5 }}
          />
        </div>
      </div>
    </div>
  );
}

export function ThemeSection({
  preferences,
  onSave,
  onPreview,
}: {
  readonly preferences: Preferences;
  readonly onSave?: (patch: {
    locale: 'hu' | 'en';
    timezone: string;
    dateFormat: string;
    weekStart: string;
    theme: string;
    version: number;
  }) => Promise<void>;
  readonly onPreview?: (theme: string) => void;
}) {
  const t = useT();
  const [selected, setSelected] = useState(preferences.theme);
  const [swatches, setSwatches] = useState<ReadonlyMap<string, ThemeSwatch>>(new Map());

  /**
   * ⚠️ **Measured once on mount, not on every render.** Each read appends a probe element and forces
   * a style recalculation; doing it per render on a tab with eight tiles is eight recalcs per
   * keystroke elsewhere on the screen.
   */
  useEffect(() => {
    setSwatches(readThemeSwatches());
  }, []);

  const choose = (theme: string): void => {
    setSelected(theme);

    /**
     * ⚠️ **Painted IMMEDIATELY, before the save resolves.** A theme that waits for a round trip
     * feels broken, and it is the one setting where the preview *is* the confirmation. The save is
     * fire-and-forget for the same reason — if it fails, the stored value is still the old one and
     * the next load corrects the screen.
     */
    /* No cast at all: `ThemeChoice` is a string union that `string` already satisfies here, so both
     * `as never` and `as ThemeChoice` were noise the lint flagged in turn. */
    applyTheme(theme);
    /* ⚠️ And cached locally as a PAINT HINT, which is the other half of no-flash. The server
     * stays the source of truth — this is only what `index.html` reads before the first frame,
     * so a reload does not go white on the way to the theme you just chose. */
    rememberTheme(theme);
    onPreview?.(theme);

    void onSave?.({
      locale: preferences.locale,
      timezone: preferences.timezone,
      dateFormat: preferences.dateFormat,
      weekStart: preferences.weekStart,
      theme,
      version: preferences.version,
    });
  };

  /* `system` is deliberately not in `THEMES` — it is a *choice*, not a palette, and keeping it out
   * is what stops it appearing as a ninth theme with no tokens of its own. */
  const tiles: readonly { id: string; label: string; hint: string }[] = [
    { id: 'system', label: t('account.theme.system'), hint: t('account.theme.systemHint') },
    ...THEMES.map((theme) => ({
      id: theme.id,
      label: t(THEME_LABEL[theme.id] ?? 'app.name'),
      hint: t(THEME_HINT[theme.id] ?? 'app.name'),
    })),
  ];

  return (
    <CardColumn>
      <Card title={t('account.theme.title')}>
        <ul className="grid gap-2 sm:grid-cols-2">
          {tiles.map((tile) => (
            <li key={tile.id}>
              <button
                type="button"
                aria-current={tile.id === selected ? 'true' : undefined}
                data-theme-tile={tile.id}
                onClick={() => {
                  choose(tile.id);
                }}
                className={[
                  'w-full rounded-lg border p-2 text-left transition-colors',
                  tile.id === selected
                    ? 'border-primary bg-accent/50'
                    : 'hover:bg-accent/30 border-input',
                ].join(' ')}
              >
                <Swatch swatch={swatches.get(tile.id)} />

                <span className="mt-2 flex items-center gap-1.5">
                  <span className="flex-1 text-[12.5px] font-medium">{tile.label}</span>
                  {tile.id === selected && (
                    <>
                      <Icon name="check" size={13} className="text-primary shrink-0" />
                      {/* The tick is the visible marker; `aria-current` covers assistive tech, and
                          this covers the case where somebody reads the label list alone. */}
                      <span className="sr-only">{t('account.theme.current')}</span>
                    </>
                  )}
                </span>
                <span className="text-muted-foreground mt-0.5 block text-[11px] leading-relaxed">
                  {tile.hint}
                </span>
              </button>
            </li>
          ))}
        </ul>

        {/* ⚠️ Said once, at the bottom, rather than on every tile: under jsdom and before the
            stylesheet loads there is nothing to measure, and eight identical placeholders with no
            explanation read as a broken gallery. */}
        {swatches.size === 0 && (
          <p className="text-muted-foreground mt-3 text-[11.5px]">{t('account.theme.noPreview')}</p>
        )}
      </Card>
    </CardColumn>
  );
}

/** Re-exported so a test can assert the roles a tile draws without importing from `packages/ui`. */
export { SWATCH_ROLES };
